import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { SelectChangeEvent } from '@mui/material';
import Grid from '@mui/material/Grid';
import { reactFlowStyles } from './reactFlowStyles';
import ReactFlow, {
  ReactFlowProvider,
  Controls,
  MiniMap,
  Background,
  applyEdgeChanges,
  applyNodeChanges,
  addEdge,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  Connection,
  useEdgesState,
  useNodesState,
  MarkerType,
  updateEdge,
} from 'react-flow-renderer';
import DecisionNode from '../decisionBox';
import ProcessNode from '../processNode';
import {
  TechnicalProfile,
  SmartActionOTP,
  ModalComponent,
  SearchComponent,
  MultiSelectComponent,
} from '../gritComponents';
import { SelectComponent, TextComponent } from '../uiComponents';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import ContextMenu from '../contextMenu';
import { deepCopy, getTPType, isCirclic } from '../../utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEdges,
  getNodes,
  setNodes as updateNodes,
  setEdges as updateEdges,
  getPolicyComponents,
  getRPComponents,
  getRPInheritance,
  loadTranformationConfig,
  getSelfAssertedProfiles,
  getOtherProfiles,
  addTechnicalProfile,
  setSelectedNodeID,
  updateJourney,
  getUserJourneys,
  getSubJourneys,
  setNodesAndEdges,
  setDebugPolicyLog,
  getFileTraverseOrder,
  setFileTraverseOrder,
  getIsDebugPolicy,
  getDebugPolicyLog,
  addDuplicateState,
  replaceDuplicateState,
  discardDuplicateState,
  getClaimTransformations,
  addUpdateClaimTransformationsList,
  getClaims,
  addClaimsList,
  setSearchResult,
  setSearchTPResult,
  setSelectedSearch,
  getJourneyNameType,
  setJourneyNameType,
} from '../../store';
import { OptionProps } from '../../view-models';
import UploadPolicyButton from '../uiComponents/uploadPolicy';
import { PolicyComponents, SubJourney, UserJourney } from '../../xmlParsing/interfaces';
import { init_OrchestrationStep } from '../../xmlParsing/initInterfaces';
import DownloadPolicyButton from '../uiComponents/downloadPolicy';
import { convertOrchestrationStepsToGraph, evalExpression } from '../../xmlParsing/convertJsonPolicyToGraph';

import { bezierEdgeFactory } from '@tisoap/react-flow-smart-edge';
import DebugPopUp from '../uiComponents/DebugPopUp';
import constants from '../../constants/constants';
import useSearch from './useSearchHook';
const StraightSmallPadding = bezierEdgeFactory({ nodePadding: 25 });
const edgeTypes = { smart: StraightSmallPadding };
const nodeTypes = { decision: DecisionNode, process: ProcessNode };

type showProps =
  | ''
  | 'nodeMenu'
  | 'edgeMenu'
  | 'paneMenu'
  | 'popup'
  | 'userJourney'
  | 'subJourney'
  | 'smartAction-OTP'
  | 'add-precondition'
  | 'add-technicalprofile';

const ReactFlowWrapper = () => {
  // const classes = reactFlowStyles();
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
  const [show, setShow] = useState<showProps>('');
  const [protocolName, setProtocolName] = useState<string>('');
  const [showDebugPopUp, setShowDebugPopUp] = useState<boolean>(false);
  const [showData, setShowData] = useState<any>({});
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [loadLogisLoading, setLoadLogisLoading] = useState<boolean>(false);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedRP, setSelectedRP] = useState<string>('');
  const [zoom, setZoom] = useState<number>(1);
  const reactFlowWrap = useRef<HTMLDivElement>(null);
  const edgeUpdateSuccessful = useRef(true);
  const selfAssertedProfiles = useSelector(getSelfAssertedProfiles);
  const otherProfiles = useSelector(getOtherProfiles);
  const userJourneys = useSelector(getUserJourneys);
  const fileTraverseOrder = useSelector(getFileTraverseOrder);
  const subJourneys = useSelector(getSubJourneys);
  const journeyNameType = useSelector(getJourneyNameType);

  const {
    searchOptions,
    searchIn,
    searchCategory,
    searchText,
    showSearchDialog,
    handleCategoryChange,
    setShowSearchDialog,
    setSearchText,
    handleTextChange,
  } = useSearch(selfAssertedProfiles, otherProfiles, userJourneys, subJourneys, journeyNameType, selectedRP);

  const dispatch = useDispatch();
  const initialNodes = useSelector(getNodes);
  const initialEdges = useSelector(getEdges);

  const [allRPOptions, setAllRPOptions] = useState<OptionProps[]>([]);

  const [selectedTP, setSelectedTP] = useState<string>('');
  const [allSelfAssertedOptions, setAllSelfAssertedOptions] = useState<OptionProps[]>([]);
  const [policy, setPolicy] = useState<PolicyComponents>();
  const policyComponents = useSelector(getPolicyComponents);
  const rpComponents = useSelector(getRPComponents);
  const rpInheritance = useSelector(getRPInheritance);
  const isDebugPolicy = useSelector(getIsDebugPolicy);
  const debugPolicyLog = useSelector(getDebugPolicyLog);
  const claimTransformations = useSelector(getClaimTransformations);
  const claims = useSelector(getClaims);

  /*
    Load the transformation configuration on load of the application.
  */
  useEffect(() => {
    dispatch(loadTranformationConfig());
  }, []);
  /* 
    Once the files are uploaded. Look for rpInheritance in the store.
    If available take the keys and populate them as user journey list.
  */
  useEffect(() => {
    if (Object.keys(rpInheritance).length > 0) {
      const res = Object.keys(rpInheritance).reduce(
        (acc: OptionProps[], curVal: any) => acc.concat([{ value: curVal, label: curVal }]),
        [],
      );
      setAllRPOptions(res);
      setSelectedRP(res[0]['value']);
    }
  }, [rpInheritance]);

  /*
    Once a userjourney is selected, generate the files to be traversed from the rpInheritance object.
  */
  useEffect(() => {
    selectedRP &&
      rpInheritance.hasOwnProperty(selectedRP) &&
      dispatch(setFileTraverseOrder([selectedRP].concat(rpInheritance[selectedRP]).reverse()));
  }, [selectedRP, rpInheritance]);

  useEffect(() => {
    if (
      selectedRP &&
      rpComponents &&
      rpComponents.hasOwnProperty(selectedRP) &&
      journeyNameType['type'] === 'UserJourneys' &&
      journeyNameType['name'] !== rpComponents[selectedRP]['defaultUserJourney']
    ) {
      dispatch(setJourneyNameType({ name: rpComponents[selectedRP]['defaultUserJourney'], type: 'UserJourneys' }));
    }
  }, [selectedRP, rpComponents, journeyNameType]);

  /*
    If any of policyComponents, rpComponents change, print them in the console for reference. 
    This is what will be used while downloading the policy files
    Also update the nodes and edges when there is some change
  */
  useEffect(() => {
    Object.keys(policyComponents).length > 0 && console.log('FINAL policyComponents changed :', policyComponents);
    selectedRP && dispatch(setNodesAndEdges(journeyNameType));
  }, [policyComponents]);

  useEffect(() => {
    Object.keys(rpComponents).length > 0 && console.log('FINAL rpComponents changed :', rpComponents);
    selectedRP && dispatch(setNodesAndEdges(journeyNameType));
  }, [rpComponents]);

  /* 
    The graph should be redrawn on below conditions:
    1. when the userJourney is updated (adding preconditions / smart actions).
    2. when new User journey is selected.
  */
  useEffect(() => {
    fileTraverseOrder.length > 0 && journeyNameType?.name && selectedRP && dispatch(setNodesAndEdges(journeyNameType));
    dispatch(setSelectedSearch(null));
  }, [journeyNameType, selectedRP]);

  useEffect(() => {
    setShowSearchDialog(false);
    setSearchText('');
    dispatch(setSearchResult([]));
    dispatch(setSearchTPResult({}));
    dispatch(setSelectedSearch(null));
  }, [selectedRP]);

  /*
    Once the reactFlow instance is rendered, centre align the content
    Called when user changes the userjourney and toggle between userjourney and subjourney
  */
  useEffect(() => {
    setTimeout(() => {
      if (reactFlowInstance && nodes.length) {
        // @ts-ignore
        reactFlowInstance.fitView();
        // @ts-ignore
        reactFlowInstance.setCenter(anchorPoint['x'] + 200, 300);
        // @ts-ignore
        reactFlowInstance.zoomTo(zoom);
        // @ts-ignore
        setZoom(reactFlowInstance.getZoom());
      }
    }, 500);
  }, [reactFlowInstance, journeyNameType, selectedRP]);

  /*
  When the self asserted profile changes, populate the list to show in the UI
  */
  useEffect(() => {
    if (Object.keys(selfAssertedProfiles).length > 0) {
      const res = Object.keys(selfAssertedProfiles).reduce(
        (acc: OptionProps[], curVal: string) => acc.concat([{ value: curVal, label: curVal }]),
        [],
      );
      setAllSelfAssertedOptions(res);
      setSelectedTP(selectedTP || res[0].value);
    }
  }, [selfAssertedProfiles]);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges]);

  const onInit = (rf: any) => {
    setReactFlowInstance(rf);
  };

  const onCloseTechnicalProfile = (data: Node) => {
    data != null ? dispatch(replaceDuplicateState()) : dispatch(discardDuplicateState());
    setShow('');
    setProtocolName('');
  };
  const onClosePrecondition = (data: any) => {
    data != null ? dispatch(replaceDuplicateState()) : dispatch(discardDuplicateState());
    setShow('');
    setShowData({});
  };

  const onCloseAddTechnicalProfile = (data: any) => {
    if (data != null && selectedRP) {
      // @ts-ignore
      data['protocol'] = constants.TECHNICAL_PROFILES[data.tpType]['protocol'];
      // @ts-ignore
      data['claimProvider'] = constants.TECHNICAL_PROFILES[data.tpType]['claimProvider'];
      // @ts-ignore
      data['metadata'] = constants.TECHNICAL_PROFILES[data.tpType]['metadata'];
      // @ts-ignore
      data['includeTechnicalProfile'] = constants.TECHNICAL_PROFILES[data.tpType]['includeTechnicalProfile'] || '';
      // @ts-ignore
      if (constants.TECHNICAL_PROFILES[data.tpType].hasOwnProperty('data')) {
        // Only for MFA => MFA has claimTransformations and claims to be checked.
        // @ts-ignore
        const _data = constants.TECHNICAL_PROFILES[data.tpType]['data'];
        const check = _data['check'] || {};
        if (check.hasOwnProperty('claimTransformations')) {
          check['claimTransformations'].forEach((clTrans: string) => {
            if (!claimTransformations.hasOwnProperty(clTrans)) {
              dispatch(addUpdateClaimTransformationsList(_data[clTrans]));
            }
          });
        }
        if (check.hasOwnProperty('claims')) {
          check['claims'].forEach((claim: string) => {
            if (!claims.hasOwnProperty(claim)) {
              dispatch(addClaimsList(_data[claim]));
            }
          });
        }
        Object.keys(_data['add'] || {}).forEach((el: string) => {
          data[el] = _data['add'][el];
        });
        (_data['delete'] || []).forEach((el: string) => {
          delete data[el];
        });
      }
      const protocolName =
        data.tpType === 'selfAssertedProfiles'
          ? constants.PROTOCOL_NAME.selfAssertedProfiles
          : constants.PROTOCOL_NAME.otherProfile;
      delete data['tpType'];
      data['id'] &&
        dispatch(
          addTechnicalProfile({
            data,
            protocolName,
          }),
        );

      let _orchStep = {
        ...init_OrchestrationStep(),
        type: 'ClaimsExchange',
        claimsExchanges: [{ id: data['id'], technicalProfilerefrenceId: data['id'] }],
        order: parseInt(showData['parentId']),
      };

      data['id'] &&
        dispatch(
          updateJourney({
            data,
            protocolName:
              data.tpType === 'selfAssertedProfiles'
                ? constants.PROTOCOL_NAME.selfAssertedProfiles
                : constants.PROTOCOL_NAME.otherProfile,
            orchStep: _orchStep,
            position: showData['position'],
            journeyNameType,
          }),
        );
      dispatch(replaceDuplicateState());
    } else {
      dispatch(discardDuplicateState());
    }
    setShow('');
    setShowData({});
  };

  // const generateID = (name: string) => {
  //   let derivedName = name;
  //   let count = 1;
  //   while (selfAssertedProfiles.hasOwnProperty(derivedName)) {
  //     derivedName = name + count;
  //     count += 1;
  //   }
  //   return derivedName;
  // };
  // const onCloseSmartActions = (data: any) => {
  //   console.log('smartAction data :', data);
  //   if (!data) {
  //     setShow('');
  //     setProtocolName('');
  //     return;
  //   }
  //   const newNodeId = generateID('OTP');
  //   let _userJourneys = JSON.parse(JSON.stringify(userJourneys));
  //   let _combinedStep = parseInt(data['step1Selection'].split('-')[0]);
  //   let _socialStep = parseInt(data['step2Selection'].split('-')[0]);
  //   // @ts-ignore
  //   _userJourneys[parseInt(selectedRP)]['orchestrationSteps'][_combinedStep - 1]['claimsProviderSelections'][
  //     'providers'
  //   ].push({
  //     targetClaimExchangeId: 'OTP' + Math.floor(Math.random() * 100),
  //     validationClaimsExchange: newNodeId,
  //   });
  //   // @ts-ignore
  //   _userJourneys[parseInt(selectedRP)]['orchestrationSteps'][_socialStep - 1]['claimsExchanges'].push({
  //     id: 'OTP' + Math.floor(Math.random() * 100),
  //     technicalProfilerefrenceId: newNodeId,
  //   });
  //   dispatch(updateJourney(_userJourneys));
  //   const id = 'node-' + guidGenerator();
  //   const newNode = {
  //     id: id,
  //     position: { x: 100, y: 600 },
  //     data: {
  //       label: newNodeId,
  //     },
  //   };
  //   dispatch(updateNodes([...nodes, newNode]));

  //   const edge1 = {
  //     id: 'edge-' + guidGenerator(),
  //     source: 'precondition0',
  //     type: 'smart',
  //     target: id,
  //     sourceHandle: 'down',
  //     style: { stroke: 'green' },
  //     markerEnd: {
  //       type: MarkerType.ArrowClosed,
  //     },
  //   };
  //   const edge2 = {
  //     id: 'edge-' + guidGenerator(),
  //     source: id,
  //     type: 'smart',
  //     target: '3',
  //     style: { stroke: 'green' },
  //     markerEnd: {
  //       type: MarkerType.ArrowClosed,
  //     },
  //   };
  //   dispatch(updateEdges([...edges, edge1, edge2]));

  //   const newOTPProfile = {
  //     ...selfAssertedProfiles['LocalAccountDiscoveryUsingEmailAddress'],
  //     id: newNodeId,
  //     displayName: newNodeId,
  //   };
  //   console.log('newOTPProfile :', newOTPProfile);

  //   dispatch(updateSelfAssertedProfile({ nodeData: newOTPProfile, selectedTP: newNodeId }));

  //   setShow('');
  //   setProtocolName('');
  // };

  const onCloseContext = (data: any) => {
    let _name = '';
    switch (data.type) {
      case 'node':
        switch (data.action) {
          case 'edit':
            // This condition will not come. Edit Node option will be shown only if the condition matches.
            // if (!selfAssertedProfiles[data.data.data.label] && !otherProfiles[data.data.data.label]) {
            //   alert(`Editing ${data.data.data.label} is Currently Not Supported `);
            //   break;
            // }

            // TODO: method to convert label to corresponding selfAssserted/invokedSubjourney/precondition data or modify nodes to include the data
            // look in otherProfiles, and selfAssertedProfiles for corresponding data to id
            setSelectedTP(data.data.data.label);
            if (selfAssertedProfiles[data.data.data.label]) {
              setShow('popup');
              setProtocolName('selfAssertedProfiles');
              dispatch(addDuplicateState());
              data && data.data && data.data.data && setShowData(selfAssertedProfiles[data.data.data.label]);
            } else {
              const othProfileData = otherProfiles[data.data.data.label];
              const protocol = othProfileData && getTPType(othProfileData.protocol, othProfileData.metadata);
              setShow('popup');
              setProtocolName(protocol);
              dispatch(addDuplicateState());
              data && data.data && data.data.data && setShowData(othProfileData);
            }
            break;
          case 'subJourney':
            dispatch(setJourneyNameType({ name: data.data.data.subJourney, type: 'SubJourneys' }));
            const _subJourney = subJourneys.filter((el: any) => el.id === data.data.data.subJourney);
            _subJourney.length > 0 ? setShowData(_subJourney[0]) : setShowData({});
            // @ts-ignore
            const { nodes, edges } = convertOrchestrationStepsToGraph(_subJourney[0]['orchestrationSteps']);
            setNodes(nodes);
            setEdges(edges);
            setShow('subJourney');
            break;
          case 'add-precondition':
            setShow('add-precondition');
            _name = '';
            if (selectedRP) {
              const _journeys = journeyNameType['type'] === 'SubJourneys' ? subJourneys : userJourneys;
              const index = _journeys.findIndex((el: UserJourney | SubJourney) => el.id === journeyNameType['name']);
              let _allPC = _journeys[index].orchestrationSteps.filter((el: any) => {
                if (!isNaN(data.data.parentId) && el.order === parseInt(data.data.parentId)) {
                  _name = data.data.data.label;
                  return true;
                }
                return false;
              });
              let _currentPC = _allPC.length > 0 ? _allPC[0] : {};
              setShowData({ ..._currentPC, label: _name });
              dispatch(addDuplicateState());
            }
            break;
          case 'add-technicalprofile':
            setShow(data.action);
            setShowData({ parentId: showData['parentId'], position: data['position'] });
            dispatch(addDuplicateState());
            break;
          case 'smartAction-OTP':
            console.log('smartAction-OTP');
            setShow('smartAction-OTP');
            dispatch(addDuplicateState());
            break;
          default:
            window.alert('This operation is not supported1');
        }
        break;
      case 'edge':
        console.log('editing edge');
        break;
      default:
        window.alert('This operation is not supported2');
        setShow('');
        setProtocolName('');
        break;
    }
  };

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      setShowData(node);
      setAnchorPoint({ x: event.pageX, y: event.pageY });
      setShow('nodeMenu');
    },
    [setAnchorPoint, setShow],
  );

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();
      setShowData(edge);
      setAnchorPoint({ x: event.pageX, y: event.pageY });
      setShow('edgeMenu');
    },
    [setAnchorPoint, setShow],
  );

  // const handleContextMenu = useCallback(
  //   (event) => {
  //     event.preventDefault();
  //     setAnchorPoint({ x: event.pageX, y: event.pageY });
  //     setShow('menu');
  //   },
  //   [setAnchorPoint, setShow]
  // );

  // useEffect(() => {
  //   document.addEventListener("contextmenu", handleContextMenu);
  //   return () => {
  //     document.removeEventListener("contextmenu", handleContextMenu);
  //   }
  // });

  const onPaneContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setAnchorPoint({ x: event.pageX, y: event.pageY });
    setShow('paneMenu');
  };
  const onNodeClick = (event: React.MouseEvent, node: Node) => {
    //pass data to DebugPopUp
    //store
    dispatch(setSelectedNodeID(node.data.label));
    //show DebugPopUp Panel
    if (node.data.label.toLowerCase().startsWith('debug-') && Object.keys(debugPolicyLog).length > 0) {
      setShowDebugPopUp(true);
    } else {
      setShowDebugPopUp(false);
    }
  };

  const onConnect = useCallback(
    (connection: Connection) => {
      /*object contains tree structure
      ex: {
        'precondition-0-1' : [targetIds...],
        '1':[targetIds...],
        ...
      }
      */
      const obj: any = {};
      edges.forEach((edge) => {
        obj[edge.source] = obj[edge.source] || [];
        obj[edge.source].push(edge.target);
      });
      /*
      add new connection if tree doesn't contain circle structure
      */
      // @ts-ignore
      if (!isCirclic(obj, connection.source, connection.target) && connection.target !== connection.source) {
        return setEdges((eds) =>
          addEdge(
            {
              ...connection,
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { stroke: connection.sourceHandle === 'right' ? 'red' : 'green' },
            },
            eds,
          ),
        );
      } else {
        return null;
      }
    },
    [setEdges, edges],
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      // @ts-ignore
      const reactFlowBounds = reactFlowWrap.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }
      // @ts-ignore
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
      /*
      add new node when we drop process or condition boxes

       */
      setNodes((nds: Node[]) => {
        const newNode: any = {
          id: (nds.length + 1).toString(),
          type,
          position,
          data: { label: `${type} node`, subJourney: '' },
        };
        return nds.concat(newNode);
      });
    },
    [reactFlowInstance, nodes],
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdate = useCallback((oldEdge, newConnection) => {
    edgeUpdateSuccessful.current = true;
    setEdges((els) => updateEdge(oldEdge, newConnection, els));
  }, []);

  const onEdgeUpdateEnd = useCallback((_, edge) => {
    if (!edgeUpdateSuccessful.current) {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }

    edgeUpdateSuccessful.current = true;
  }, []);

  return (
    <>
      {show === 'popup' && (
        <TechnicalProfile
          parentTP=""
          protocolName={protocolName}
          selectedTP={selectedTP}
          closeTechnicalProfile={onCloseTechnicalProfile}
        />
      )}
      {/* {show === 'smartAction-OTP' && (
        <SmartActionOTP userJourneyIndex={parseInt(selectedRP)} nodeData={showData} close={onCloseSmartActions} />
      )} */}
      {show === 'add-precondition' && (
        <ModalComponent
          header={'Add Preconditions for ' + showData['label']}
          type="add-precondition"
          data={{
            preconditions: showData['preconditions'] || [],
            order: showData['order'],
            journeyName: journeyNameType['name'],
          }}
          journeyType={journeyNameType['type']}
          close={onClosePrecondition}
        />
      )}
      {show === 'add-technicalprofile' && (
        <ModalComponent
          header="Add Technical Profile"
          // @ts-ignore
          type={show}
          data={showData}
          journeyType={journeyNameType['type']}
          close={(data: any) => {
            onCloseAddTechnicalProfile(data);
          }}
        />
      )}
      {show === 'nodeMenu' && (
        <ContextMenu type="nodeMenu" position={anchorPoint} data={showData} close={onCloseContext} />
      )}
      {/* {show === "edgeMenu" && <ContextMenu type="edgeMenu" position={anchorPoint} data={showData} close={onCloseContext} />}
      {show === "paneMenu" && <ContextMenu type="paneMenu" position={anchorPoint} data={showData} close={onCloseContext} />} */}
      {showDebugPopUp && <DebugPopUp />}
      {Object.keys(rpInheritance).length > 0 ? (
        <Grid container justifyContent="space-between" id="second-header">
          <Grid item xs>
            <Grid container>
              <>
                <UploadPolicyButton type="PlusUpload" />
                <DownloadPolicyButton policies={policyComponents} rpPolicies={rpComponents} />
              </>
              {Object.keys(rpInheritance).length > 0 && (
                <>
                  <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                      setSelectedRP(e.target.value);
                      dispatch(
                        setJourneyNameType({
                          name: selectedRP || (allRPOptions.length > 0 ? allRPOptions[0].value : ''),
                          type: 'UserJourneys',
                        }),
                      );
                    }}
                    data={{
                      style: { width: 200 },
                      value: selectedRP || (allRPOptions.length > 0 ? allRPOptions[0].value : ''),
                      label: 'User Journeys',
                      options: allRPOptions,
                    }}
                  />

                  {/* <SelectComponent
                    changeHandler={(e: SelectChangeEvent<string>) => {
                      setSelectedTP(e.target.value);
                    }}
                    data={{
                      style: { width: 200 },
                      value: selectedTP || (allSelfAssertedOptions.length > 0 ? allSelfAssertedOptions[0].value : ''),
                      label: 'Self Asserted Profile',
                      options: allSelfAssertedOptions,
                    }}
                  /> */}
                  {/* <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setShow('popup');
                      setProtocolName('selfAssertedProfiles');
                      dispatch(addDuplicateState());
                    }}
                  >
                    Edit
                  </Button> */}
                  {/* Only show if debug policy is loaded */}
                  {isDebugPolicy && (
                    <LoadingButton
                      loading={loadLogisLoading}
                      style={{ marginLeft: '1rem' }}
                      variant="outlined"
                      color="primary"
                      onClick={async () => {
                        setLoadLogisLoading(true);
                        // call Log api
                        //probably should name function and move out of index file
                        // https://charlottetestapp.azurewebsites.net/api/ReadLogs
                        // TODO : tenantObjectId needs to be filled from hardcoded xml or xml in combination with appsettings file.
                        let body = JSON.stringify({
                          tenantObjectId: '21621df9-a140-422e-87b4-80b93c485d5f',
                          monthYear: '5/2022',
                          key: '',
                          value1: '',
                          value2: '',
                          reducer: 'objectId',
                        });
                        let myHeaders = new Headers();
                        myHeaders.append('Content-Type', 'application/json');
                        let requestOptions: any = {
                          method: 'POST',
                          headers: myHeaders,
                          body: body,
                          redirect: 'follow',
                        };

                        let users = await fetch(
                          'https://charlottetestapp.azurewebsites.net/api/ReadLogs',
                          requestOptions,
                        )
                          .then((response) => response.text())
                          .then((result) => {
                            console.log(result);
                            return JSON.parse(result);
                          })
                          .catch((error) => console.log('error', error));
                        // need to resolve user id from another request or make user provide the input for the user id/objectid.
                        // users[0]
                        // get newest objectID entry
                        let newestDate = 0;
                        let newestUserObjectId;
                        for (let i = 0; i < users.length; i++) {
                          // Etag format "W/\"datetime'2022-05-24T00%3A05%3A36.9018885Z'\""
                          let userTimestamp = Date.parse(decodeURIComponent(users[i].databaseEtag).split("'")[1]);
                          if (userTimestamp > newestDate) {
                            newestDate = userTimestamp;
                            newestUserObjectId = users[i].objectId;
                          }
                        }

                        // get user logs
                        body = JSON.stringify({
                          tenantObjectId: '21621df9-a140-422e-87b4-80b93c485d5f',
                          monthYear: '5/2022',
                          key: 'objectId',
                          value1: newestUserObjectId,
                          value2: '',
                          reducer: '',
                        });

                        requestOptions = {
                          method: 'POST',
                          headers: myHeaders,
                          body: body,
                          redirect: 'follow',
                        };

                        let lastUserLogs = await fetch(
                          'https://charlottetestapp.azurewebsites.net/api/ReadLogs',
                          requestOptions,
                        )
                          .then((response) => response.text())
                          .then((result) => {
                            console.log(result);
                            return JSON.parse(result);
                          })
                          .catch((error) => console.log('error', error));

                        let newestLogDate = 0;
                        let lastCorrelationID: string;

                        //in the future this can be re written to put correlation ids in buckets
                        for (let index = 0; index < lastUserLogs.length; index++) {
                          const log = lastUserLogs[index];
                          const logDate = Date.parse(log.databaseTimestamp);
                          if (logDate > newestLogDate) {
                            newestLogDate = logDate;
                            lastCorrelationID = log.debugCorrelationId;
                          }
                        }
                        let lastUserLog = lastUserLogs.filter(
                          (log: any) => log.debugCorrelationId == lastCorrelationID,
                        );

                        //set userlog and call to update edges
                        dispatch(setDebugPolicyLog({ lastUserLog }));

                        //update edges based on DebugPolicyLog

                        // if isDebugPolicy
                        // debugStepIndex had 1:1 relation with DebugSATP
                        // give preconditions edges as part of data,
                        // Label Debug stepsEgde ids not needed can correlate number with nodeis and check label if debug
                        // for every edge if prcondition solve path stop if precondition or debug
                        // precondition-orchstep-nthprecondition
                        // add debug prefix to debug node ids or resolve number to see if it is a debug node

                        let nodes = deepCopy(initialNodes);
                        let edges = deepCopy(initialEdges);

                        // if src or dst is debug
                        for (let i = 0; i < edges.length; i++) {
                          //source:"1"
                          //target:"precondition-0-0" orchstep-nthprecondition
                          //stroke:"green" || "red"
                          let edge = edges[i];
                          //edge needs orchstep number to see next
                          if (edge.source.toLowerCase().startsWith('precondition')) {
                            const [, orchstep, nthPrecondition] = edge.source.split('-');
                            // find precondition statement
                            let condition = '';
                            for (let j = 0; j < nodes.length; j++) {
                              // this can be optimized by saving the condition in the edge
                              const node = nodes[j];
                              if (node.id == edge.source) {
                                //precondition node
                                condition = node.data.label;
                                break;
                              }
                            }
                            // orchstep+2; //to correlate stepindex to debug orchstep
                            let colorGreen =
                              condition == '' ||
                              evalExpression(condition, (parseInt(orchstep) + 2).toString(), lastUserLog);
                            //target is not debug flip colorGreen
                            if (edge.target.toLowerCase().startsWith('debug')) {
                              colorGreen = !colorGreen;
                            }
                            if (colorGreen) {
                              edges[i].style = { stroke: 'green' };
                            } else {
                              edges[i].style = { stroke: 'lightgrey' };
                            }
                            // in the case that there are multiple tps verically, which afaik dosen't happen then the following would need to be done
                            // follow edge path and use same color untill target is debug then remove the elseif as it would no longer be needed.
                          } else if (edge.target.toLowerCase().startsWith('debug')) {
                            // edge step-=2 is precondition then recolor
                            // pred - orch -lastone
                            // take last digit of debug edge and match with precondition node
                            const [, orchStep] = edge.target.split('-');
                            const preconditionStep = (parseInt(orchStep) - 2).toString();
                            // find the last precondition node that matches the preconditionStep
                            let condition = '';
                            for (let j = 0; j < nodes.length; j++) {
                              const node = nodes[j];
                              if (
                                node.id.toLowerCase().startsWith('precondition') &&
                                node.id.split('-')[1] == preconditionStep
                              ) {
                                //precondition node
                                condition = node.data.label;
                              }
                            }
                            // correlate stepindex to debug orchstep
                            let colorGreen = condition == '' || evalExpression(condition, orchStep, lastUserLog); // update evalExpression to use the pulled log data
                            // nasty bug was found, gotta hate typos
                            // console.log(edge)
                            // console.log(condition)
                            // console.log(orchStep)
                            // console.log(colorGreen)
                            if (colorGreen) {
                              edges[i].style = { stroke: 'green' };
                            } else {
                              edges[i].style = { stroke: 'lightgrey' };
                            }
                          }
                        }
                        dispatch(updateNodes(nodes));
                        dispatch(updateEdges(edges));
                        setLoadLogisLoading(false);
                      }}
                    >
                      Load Log
                    </LoadingButton>
                  )}
                </>
              )}
            </Grid>
          </Grid>
          <Grid item xs="auto">
            <Grid container justifyContent="flex-end">
              {Object.keys(rpInheritance).length > 0 && (
                <>
                  <MultiSelectComponent
                    options={searchOptions}
                    searchIn={searchIn}
                    selected={searchCategory}
                    handleChange={(searchIn: 'rpFiles' | 'userJourney', val: any) => {
                      handleCategoryChange(searchIn, val);
                    }}
                  />
                  <TextComponent
                    changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                      handleTextChange(e.target.value);
                    }}
                    data={{
                      value: searchText,
                      label: 'Search Text',
                    }}
                  />
                </>
              )}
              {journeyNameType['type'] === 'SubJourneys' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShow('');
                    setProtocolName('');
                    setShowData('');
                    dispatch(setJourneyNameType({ ...journeyNameType, type: 'UserJourneys' }));
                    setNodes(initialNodes);
                    setEdges(initialEdges);
                  }}
                >
                  Back to User Journeys
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <UploadPolicyButton type="InitUpload" />
        </Grid>
      )}

      {showSearchDialog && (
        <SearchComponent
          header={"Results for '" + searchText + "'"}
          searchText={searchText}
          searchCategory={searchCategory}
          close={(data: any) => {
            if (data === null) {
              setShowSearchDialog(false);
              setSearchText('');
              dispatch(setSearchResult([]));
              dispatch(setSearchTPResult({}));
              dispatch(setSelectedSearch(null));
            } else {
              const _validateName = data.hasOwnProperty('orchStepName') ? data['orchStepName'] : data['precondition'];
              const node = initialNodes.filter((node: any) => node['data']['label'].includes(_validateName))[0];
              node &&
                setTimeout(() => {
                  if (reactFlowInstance && nodes.length) {
                    let yPos: number = node['position']['y'];
                    if (zoom < 1) {
                      yPos = yPos < 400 ? yPos + 400 : yPos;
                    } else {
                      yPos = yPos > 600 ? yPos - 300 : yPos;
                    }
                    // @ts-ignore
                    reactFlowInstance.setCenter(anchorPoint['x'] + 200, yPos);
                    // @ts-ignore
                    reactFlowInstance.zoomTo(zoom);
                  }
                }, 0);
            }
          }}
        />
      )}
      {nodes.length > 0 && (
        <>
          <div className="dndflow">
            <ReactFlowProvider>
              <div className="reactflow-wrapper" ref={reactFlowWrap}>
                <ReactFlow
                  onInit={onInit}
                  fitView
                  nodes={nodes}
                  edges={edges}
                  edgeTypes={edgeTypes}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  nodeTypes={nodeTypes}
                  onNodeClick={onNodeClick}
                  onNodeContextMenu={onNodeContextMenu}
                  onEdgeContextMenu={onEdgeContextMenu}
                  onPaneContextMenu={onPaneContextMenu}
                  onEdgeUpdate={onEdgeUpdate}
                  onEdgeUpdateStart={onEdgeUpdateStart}
                  onEdgeUpdateEnd={onEdgeUpdateEnd}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  // zoomOnScroll={false}
                  onPaneClick={() => {
                    setShow('');
                    setProtocolName('');
                    setShowDebugPopUp(false);
                  }}
                  // style={rfStyle}
                >
                  <MiniMap />
                  <Controls />
                  <Background />
                </ReactFlow>
              </div>
            </ReactFlowProvider>
          </div>
        </>
      )}
    </>
  );
};

export { ReactFlowWrapper };
